import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Layers, ArrowLeft, Rss, Sparkles, Loader2, Pencil, RefreshCw, Facebook, Activity,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    getCategoryConfig, getCategoryFeeds, setCategoryFeeds,
    listRssFeeds, syncAllPagesForCategory, listCategoryPages, listCategoryPagesHealth,
    type SocialCategoryConfig, type SocialPageHealth,
} from '@/services/api/social-publisher';
import CategoryConfigForm from './components/CategoryConfigForm';


export default function CategoryConfigDetail() {
    const { id } = useParams<{ id: string }>();
    const configId = Number(id);
    const qc = useQueryClient();
    const { toast } = useToast();
    const [editOpen, setEditOpen] = useState(false);

    const { data: cfg, isLoading } = useQuery({
        queryKey: ['social-publisher', 'category-config', configId],
        queryFn: () => getCategoryConfig(configId),
        enabled: !!configId,
    });

    const { data: feedIds = [] } = useQuery({
        queryKey: ['social-publisher', 'category-feeds', configId],
        queryFn: () => getCategoryFeeds(configId),
        enabled: !!configId,
    });

    const { data: allFeeds = [] } = useQuery({
        queryKey: ['social-publisher', 'rss-feeds'],
        queryFn: listRssFeeds,
    });

    const { data: pages = [], isLoading: pagesLoading } = useQuery({
        queryKey: ['social-publisher', 'category-pages', configId],
        queryFn: () => listCategoryPages(configId),
        enabled: !!configId,
    });

    // Pré-check de saúde (on-demand) — probe vivo no Graph API por página.
    const [health, setHealth] = useState<Record<number, SocialPageHealth>>({});
    const healthMut = useMutation({
        mutationFn: () => listCategoryPagesHealth(configId),
        onSuccess: (rows) => {
            const m: Record<number, SocialPageHealth> = {};
            rows.forEach((h) => { m[h.page_pk] = h; });
            setHealth(m);
            const blocked = rows.filter((h) => !h.can_publish).length;
            toast({
                title: blocked === 0
                    ? `Todas as ${rows.length} páginas OK pra publicar`
                    : `${blocked} de ${rows.length} páginas NÃO vão publicar`,
                description: blocked > 0 ? 'Veja o motivo em cada página abaixo.' : undefined,
                variant: blocked > 0 ? 'destructive' : undefined,
            });
        },
        onError: (e: any) => toast({
            title: 'Erro ao checar saúde',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    const setFeedsMut = useMutation({
        mutationFn: (nextIds: number[]) => setCategoryFeeds(configId, nextIds),
        onSuccess: () => {
            toast({ title: 'Feeds atualizados' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'category-feeds', configId] });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'category-config', configId] });
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    const syncMut = useMutation({
        mutationFn: () => syncAllPagesForCategory(configId),
        onSuccess: (res) => {
            const d = res.data;
            toast({
                title: `${d?.total_enqueued || 0} gerações enfileiradas`,
                description: res.message || undefined,
            });
            // a sync pode ter criado posts/configs novos → atualiza a lista de páginas
            qc.invalidateQueries({ queryKey: ['social-publisher', 'category-pages', configId] });
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    const toggleFeed = (feedId: number) => {
        const next = feedIds.includes(feedId)
            ? feedIds.filter((f) => f !== feedId)
            : [...feedIds, feedId];
        setFeedsMut.mutate(next);
    };

    if (isLoading) {
        return (
            <div className="p-6 text-sm text-gray-500">Carregando...</div>
        );
    }
    if (!cfg) {
        return (
            <div className="p-6">
                <p className="text-sm text-gray-500">Categoria não encontrada.</p>
                <Link to="/meta/social-publisher" className="text-purple-600 text-sm underline">
                    Voltar
                </Link>
            </div>
        );
    }

    const persona: any = cfg.persona_json || {};

    return (
        <div className="p-6 space-y-6 max-w-6xl mx-auto">
            <div className="flex items-center gap-3">
                <Link to="/meta/social-publisher" className="text-gray-400 hover:text-gray-600">
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                    <Layers className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-bold text-gray-800 truncate">
                        {cfg.category_name || `Categoria #${cfg.category_id}`}
                    </h1>
                    <p className="text-xs text-gray-500">
                        {cfg.valid_pages_count ?? 0} pages ativas ·{' '}
                        Posts: {(cfg.post_schedule_times || []).join(', ') || '—'} ·{' '}
                        Stories: {(cfg.story_schedule_times || []).join(', ') || '—'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button
                        onClick={() => syncMut.mutate()}
                        disabled={syncMut.isPending}
                        className="bg-purple-600 hover:bg-purple-700"
                    >
                        {syncMut.isPending ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sincronizando...</>
                        ) : (
                            <><Sparkles className="w-4 h-4 mr-2" /> Sincronizar TODAS as pages</>
                        )}
                    </Button>
                </div>
            </div>

            {/* Persona */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">🎭 Persona</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <div className="text-xs text-gray-400">Idioma</div>
                        <div>{persona.language || '—'}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400">Tom</div>
                        <div>{persona.tone || '(sem tom específico)'}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400">Horizonte</div>
                        <div>{cfg.horizon_days} dias</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400">Max/dia por page</div>
                        <div>{cfg.max_per_day}</div>
                    </div>
                </div>
                <div className="mt-4">
                    <div className="text-xs text-gray-400 mb-1">Hashtags</div>
                    <div className="flex flex-wrap gap-1.5">
                        {(persona.hashtags || []).length === 0 ? (
                            <span className="text-xs text-gray-400">Nenhuma</span>
                        ) : (
                            (persona.hashtags || []).map((h: string) => (
                                <span key={h} className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
                                    {h}
                                </span>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Páginas da categoria — clique pra ver os posts de cada uma */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Facebook className="w-4 h-4 text-purple-500" />
                        Páginas da categoria{' '}
                        <span className="text-gray-400 font-normal">({pages.length})</span>
                    </h2>
                    {pages.length > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => healthMut.mutate()}
                            disabled={healthMut.isPending}
                            title="Checa token e restrição de cada página no Facebook antes de você sincronizar/gerar"
                        >
                            {healthMut.isPending ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Checando…</>
                            ) : (
                                <><Activity className="w-3.5 h-3.5 mr-1.5" /> Verificar saúde</>
                            )}
                        </Button>
                    )}
                </div>
                {pagesLoading ? (
                    <div className="text-sm text-gray-400 py-4">Carregando páginas…</div>
                ) : pages.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4">Nenhuma página nessa categoria.</div>
                ) : (
                    <div className="space-y-2">
                        {pages.map((p) => {
                            const canView = !!p.page_config_id && p.posts_count > 0;
                            const inner = (
                                <div
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                        canView
                                            ? 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/40'
                                            : 'border-gray-100'
                                    }`}
                                >
                                    {p.picture_url ? (
                                        <img
                                            src={p.picture_url}
                                            alt=""
                                            className="w-9 h-9 rounded-full object-cover border border-purple-100"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                                            <Facebook className="w-4 h-4" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-800 truncate">
                                            {p.page_name || `page_pk #${p.page_pk}`}
                                        </div>
                                        <div className="text-[11px] text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span>
                                                {p.posts_count} {p.posts_count === 1 ? 'post' : 'posts'}
                                            </span>
                                            {!p.has_valid_token && (
                                                <span className="text-amber-600">· sem token</span>
                                            )}
                                            <HealthBadge h={health[p.page_pk]} />
                                        </div>
                                    </div>
                                    {canView ? (
                                        <span className="text-xs text-purple-600 font-medium shrink-0">
                                            Ver posts →
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-gray-400 shrink-0">
                                            sem posts ainda
                                        </span>
                                    )}
                                </div>
                            );
                            return canView ? (
                                <Link
                                    key={p.page_pk}
                                    to={`/meta/social-publisher/configs/${p.page_config_id}?tab=posts`}
                                >
                                    {inner}
                                </Link>
                            ) : (
                                <div key={p.page_pk}>{inner}</div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Feeds vinculados */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-800">
                        <Rss className="inline w-4 h-4 mr-1 text-purple-500" />
                        Feeds RSS vinculados{' '}
                        <span className="text-gray-400 font-normal">({feedIds.length})</span>
                    </h2>
                    <Link to="/meta/social-publisher/feeds" className="text-xs text-purple-600 hover:underline">
                        Gerenciar feeds →
                    </Link>
                </div>
                {allFeeds.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500">
                        Você ainda não cadastrou nenhum feed RSS.{' '}
                        <Link to="/meta/social-publisher/feeds" className="text-purple-600 underline">
                            Cadastrar agora
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {allFeeds.map((f) => {
                            const linked = feedIds.includes(f.id);
                            return (
                                <label
                                    key={f.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        linked
                                            ? 'bg-purple-50 border-purple-300'
                                            : 'bg-white border-gray-200 hover:border-purple-200'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={linked}
                                        onChange={() => toggleFeed(f.id)}
                                        disabled={setFeedsMut.isPending}
                                        className="w-4 h-4 accent-purple-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-800 truncate">
                                            {f.name || f.feed_url}
                                        </div>
                                        <div className="text-[11px] text-gray-500 truncate">
                                            {f.feed_url}
                                        </div>
                                    </div>
                                    {f.status !== 'active' && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                            {f.status}
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Resultado por page (última sync) */}
            {syncMut.data?.data?.per_page && syncMut.data.data.per_page.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                    <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-purple-500" />
                        Resultado da última sincronização
                    </h2>
                    <div className="space-y-1.5">
                        {syncMut.data.data.per_page.map((r) => (
                            <div key={r.page_pk} className="flex items-center justify-between text-xs border-b border-gray-50 py-1.5">
                                <span className="text-gray-700 truncate flex-1 mr-3">
                                    {r.page_name || `page_pk ${r.page_pk}`}
                                </span>
                                <span className={
                                    r.reason === 'enqueued' ? 'text-emerald-600'
                                    : r.reason === 'pulling_feeds' ? 'text-amber-600'
                                    : r.reason === 'queue_full' ? 'text-blue-600'
                                    : 'text-gray-500'
                                }>
                                    {r.reason === 'enqueued' && `${r.enqueued} gerações`}
                                    {r.reason === 'pulling_feeds' && 'puxando RSS'}
                                    {r.reason === 'queue_full' && 'fila cheia'}
                                    {r.reason === 'no_token' && 'sem token'}
                                    {r.reason === 'no_feeds' && 'sem feeds'}
                                    {r.reason === 'no_items_after_retries' && 'sem items'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <CategoryConfigForm
                open={editOpen}
                onOpenChange={setEditOpen}
                editing={cfg as SocialCategoryConfig}
                onSaved={() => {
                    setEditOpen(false);
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'category-config', configId] });
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'category-configs'] });
                }}
            />
        </div>
    );
}

// Badge de saúde da página (resultado do pré-check). title = detalhe/motivo.
function HealthBadge({ h }: { h?: SocialPageHealth }) {
    if (!h) return null;
    const styles: Record<string, { label: string; cls: string }> = {
        ok: { label: '✅ Pode publicar', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        restrita: { label: '⛔ Conta restrita', cls: 'bg-red-50 text-red-700 border-red-200' },
        sem_token: { label: '🔑 Sem token', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
        token_invalido: { label: '❌ Token inválido', cls: 'bg-red-50 text-red-700 border-red-200' },
        erro: { label: '⚠️ Erro ao checar', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    };
    const s = styles[h.status] || styles.erro;
    return (
        <span
            className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${s.cls}`}
            title={h.detail || ''}
        >
            {s.label}
        </span>
    );
}
